import React, { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useMobile } from '../hooks/useMobile';
import { api, BACKEND } from '../utils/api';
import { Toast } from './Toast';
import { LogsModal } from './LogsModal';
import { LogoIcon } from '../assets/LogoIcon';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

interface DashboardProps {
  onLogout: () => void;
}

export function Dashboard({ onLogout }: DashboardProps) {
  const mobile  = useMobile();
  const tablet  = useMobile(1100);

  const [pods,          setPods]          = useState<any[]>([]);
  const [nodes,         setNodes]         = useState<any[]>([]);
  const [deployments,   setDeployments]   = useState<any[]>([]);
  const [namespaces,    setNamespaces]    = useState<any[]>([]);
  const [events,        setEvents]        = useState<any[]>([]);
  const [metrics,       setMetrics]       = useState<any[]>([]);
  const [historical,    setHistorical]    = useState<any[]>([]);
  const [alertHistory,  setAlertHistory]  = useState<any[]>([]);
  const [rules,         setRules]         = useState<any[]>([]);
  const [pinned,        setPinned]        = useState<Set<string>>(new Set());
  const [notifications, setNotifications] = useState<any[]>([]);
  const [connected,     setConnected]     = useState(false);
  const [lastUpdate,    setLastUpdate]    = useState<Date | null>(null);
  const [score,         setScore]         = useState(100);
  const [theme,         setTheme]         = useState<'dark' | 'light'>('dark');
  const [logsFor,       setLogsFor]       = useState<any>(null);
  const [showAlerts,    setShowAlerts]    = useState(false);
  const [dashTitle,     setDashTitle]     = useState('K3s Dashboard');
  const [dashSubtitle,  setDashSubtitle]  = useState('Remote Cluster Monitor');

  const socketRef   = useRef<Socket | null>(null);
  const initialized = useRef(false);

  const [chartHours,   setChartHours]   = useState<number>(1);
  const [chartData,    setChartData]    = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartMetrics, setChartMetrics] = useState({ running: true, crashed: true, pending: true, score: false });

  const location   = useLocation();
  const navigate   = useNavigate();
  const activeTab  = location.pathname.split('/')[1] || 'pods';

  const dark = theme === 'dark';
  const bg   = dark ? '#0f172a' : '#f1f5f9';
  const card = dark ? '#1e293b' : '#ffffff';
  const text = dark ? '#e2e8f0' : '#1e293b';
  const sub  = dark ? '#64748b' : '#94a3b8';
  const bord = dark ? '#334155' : '#e2e8f0';

  const handleLogout = () => { socketRef.current?.disconnect(); onLogout(); };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const token  = localStorage.getItem('k3s_token') || '';
    const socket = io(BACKEND, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('auth_error', () => handleLogout());

    const applyData = (d: any) => {
      if (d.pods)        setPods(d.pods);
      if (d.nodes)       setNodes(d.nodes);
      if (d.deployments) setDeployments(d.deployments);
      if (d.namespaces)  setNamespaces(d.namespaces);
      if (d.events)      setEvents(d.events);
      if (d.metrics)     setMetrics(d.metrics);
      if (d.historical) {
        setHistorical(prev => {
          const prevLast = prev[prev.length - 1];
          const nextLast = d.historical[d.historical.length - 1];
          // Only replace the array if the latest data point actually changed,
          // so charts don't re-render every 5s when nothing new landed yet.
          if (prev.length === d.historical.length && prevLast?.ts === nextLast?.ts) return prev;
          return d.historical;
        });
      }
      if (d.healthScore !== undefined) setScore(d.healthScore);
      setLastUpdate(new Date());
    };

    socket.on('snapshot',      applyData);
    socket.on('update',        applyData);
    socket.on('alert_history', setAlertHistory);
    socket.on('rules',         setRules);
    socket.on('pinned',        (arr: string[]) => setPinned(new Set(arr)));
    socket.on('settings',      (s: any) => {
      if (s?.dashboardTitle)    setDashTitle(s.dashboardTitle);
      if (s?.dashboardSubtitle) setDashSubtitle(s.dashboardSubtitle);
    });
    socket.on('notifications', (changes: any[]) => {
      const notes = changes.map(c => ({ ...c, id: Date.now() + Math.random() }));
      setNotifications(p => [...notes, ...p].slice(0, 30));
      notes.filter(n => n.severity === 'critical').forEach(n => {
        if (Notification.permission === 'granted') new Notification('K3s Alert', { body: n.message });
      });
    });

    Notification.requestPermission().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chartHours <= 1) return;
    setChartLoading(true);
    api(`/api/history/chart?hours=${chartHours}`)
      .then(data => { setChartData(data); setChartLoading(false); })
      .catch(() => setChartLoading(false));
  }, [chartHours]);

  const counts = {
    total:   pods.length,
    running: pods.filter(p => p.status === 'running').length,
    crashed: pods.filter(p => ['crashed', 'failed', 'oom-killed', 'image-error'].includes(p.status)).length,
    pending: pods.filter(p => p.status === 'pending').length,
  };

  const scoreColor = score >= 90 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const scoreBg    = score >= 90 ? '#14532d' : score >= 60 ? '#78350f' : '#7f1d1d';

  const tabStyle = (t: string): React.CSSProperties => ({
    padding: mobile ? '6px 10px' : '7px 14px',
    borderRadius: 7,
    cursor: 'pointer',
    fontSize: mobile ? 12 : 13,
    fontWeight: 600,
    border: 'none',
    whiteSpace: 'nowrap',
    background: activeTab === t ? '#2563eb' : 'transparent',
    color: activeTab === t ? '#fff' : sub,
    transition: 'background 0.15s, color 0.15s',
  });

  const tabs = [
    { id: 'pods',        label: 'Pods',        count: pods.length },
    { id: 'nodes',       label: 'Nodes',       count: nodes.length },
    { id: 'deployments', label: 'Deployments', count: deployments.length },
    { id: 'namespaces',  label: 'Namespaces',  count: namespaces.length },
    { id: 'events',      label: 'Events',      count: events.length },
    { id: 'metrics',     label: 'Metrics' },
    { id: 'history',     label: 'Charts' },
    { id: 'alerts',      label: 'Alerts',      count: alertHistory.length },
    { id: 'rules',       label: 'Rules',       count: rules.length },
    { id: 'settings',    label: 'Settings' },
  ];

  // Health score explanation
  const healthExplanation = `${score}% — Pods: ${counts.running}/${counts.total} running (70%), Nodes: ${nodes.filter(n => n.status === 'online').length}/${nodes.length} online (30%)`;

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text, fontFamily: 'Inter, system-ui, sans-serif', padding: mobile ? 12 : 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LogoIcon size={mobile ? 32 : 38} />
          <div>
            <h1 style={{ margin: 0, fontSize: mobile ? 15 : 18, fontWeight: 800, letterSpacing: -0.3 }}>{dashTitle}</h1>
            {!mobile && <div style={{ fontSize: 11, color: sub, marginTop: 1 }}>{dashSubtitle}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: mobile ? 8 : 12, flexWrap: 'wrap' }}>
          {/* Health Score */}
          <div
            title={healthExplanation}
            style={{
              background: scoreBg + '55',
              border: `1px solid ${scoreColor}55`,
              borderRadius: 8,
              padding: '5px 12px',
              textAlign: 'center',
              cursor: 'help',
            }}
          >
            <div style={{ fontSize: mobile ? 15 : 18, fontWeight: 800, color: scoreColor }}>{score}%</div>
            <div style={{ fontSize: 10, color: sub, fontWeight: 600, letterSpacing: 0.3 }}>HEALTH</div>
          </div>

          {/* Connection status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: connected ? '#22c55e' : '#ef4444',
              boxShadow: connected ? '0 0 8px #22c55e88' : 'none',
            }} />
            <span style={{ fontSize: 12, color: connected ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
              {connected ? 'Live' : 'Offline'}
            </span>
          </div>

          {!mobile && lastUpdate && (
            <span style={{ fontSize: 11, color: sub }}>Updated {lastUpdate.toLocaleTimeString()}</span>
          )}

          {/* Mobile alerts toggle */}
          {mobile && (
            <button
              onClick={() => setShowAlerts(s => !s)}
              style={{
                background: showAlerts ? '#2563eb' : card,
                border: `1px solid ${bord}`,
                color: showAlerts ? '#fff' : text,
                borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 14,
                position: 'relative',
              }}
            >
              {notifications.length > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4, background: '#ef4444',
                  borderRadius: '50%', width: 16, height: 16, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700,
                }}>
                  {notifications.length > 9 ? '9+' : notifications.length}
                </span>
              )}
              Alerts
            </button>
          )}

          {/* Theme toggle */}
          <button
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              background: card, border: `1px solid ${bord}`,
              color: text, borderRadius: 8, padding: '6px 10px',
              cursor: 'pointer', fontSize: 14, lineHeight: 1,
            }}
          >
            {dark ? '☀' : '☾'}
          </button>

          <button
            onClick={handleLogout}
            style={{
              background: 'transparent', border: `1px solid ${bord}`,
              color: sub, borderRadius: 8, padding: '6px 14px',
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = dark ? '#1e293b' : '#e2e8f0')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Mobile alerts drawer */}
      {mobile && showAlerts && (
        <div style={{ background: card, borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${bord}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              Live Alerts
              {notifications.length > 0 && (
                <span style={{ marginLeft: 8, background: '#ef4444', borderRadius: 10, padding: '1px 7px', fontSize: 10, color: '#fff' }}>
                  {notifications.length}
                </span>
              )}
            </div>
            {notifications.length > 0 && (
              <button
                onClick={() => setNotifications([])}
                style={{ background: 'none', border: `1px solid ${bord}`, color: sub, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {notifications.length === 0
              ? <div style={{ textAlign: 'center', padding: 16, color: sub, fontSize: 13 }}>No recent alerts</div>
              : notifications.map(n => <Toast key={n.id} note={n} onClose={() => setNotifications(p => p.filter(x => x.id !== n.id))} />)
            }
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: mobile ? 10 : 14, marginBottom: mobile ? 14 : 20 }}>
        {[
          { label: 'Total Pods', value: counts.total,   color: '#3b82f6', bg: '#1e40af22' },
          { label: 'Running',    value: counts.running,  color: '#22c55e', bg: '#14532d22' },
          { label: 'Unhealthy',  value: counts.crashed,  color: '#ef4444', bg: '#7f1d1d22' },
          { label: 'Pending',    value: counts.pending,  color: '#f59e0b', bg: '#78350f22' },
        ].map(c => (
          <div key={c.label} style={{
            background: card, borderRadius: 12,
            padding: mobile ? '12px 14px' : '16px 20px',
            border: `1px solid ${c.color}33`,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ fontSize: mobile ? 28 : 34, fontWeight: 800, color: c.color, lineHeight: 1, margin: '0 0 4px' }}>{c.value}</div>
            <div style={{ fontSize: mobile ? 11 : 12, color: sub, fontWeight: 500 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Main layout */}
      <div style={{ display: 'grid', gridTemplateColumns: tablet ? '1fr' : '1fr 280px', gap: 20 }}>
        <div style={{ minWidth: 0 }}>
          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 4, marginBottom: 16,
            overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' as any,
            background: card, borderRadius: 10, padding: '6px 8px', border: `1px solid ${bord}`,
          }}>
            {tabs.map(t => (
              <button key={t.id} style={tabStyle(t.id)} onClick={() => navigate(`/${t.id}`)}>
                {t.label}
                {t.count !== undefined && (
                  <span style={{
                    marginLeft: 5,
                    background: activeTab === t.id ? 'rgba(255,255,255,0.2)' : bord,
                    borderRadius: 10, padding: '1px 6px', fontSize: 10,
                  }}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <Outlet context={{
            pods, nodes, deployments, namespaces, events, metrics, historical,
            alertHistory, setAlertHistory, rules, setRules, pinned, setPinned,
            theme, mobile, tablet, setLogsFor, chartHours, setChartHours,
            chartData, chartLoading, chartMetrics, setChartMetrics,
          }} />
        </div>

        {/* Desktop sidebar — notifications */}
        {!tablet && (
          <div>
            <div style={{ background: card, borderRadius: 12, border: `1px solid ${bord}`, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${bord}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  Live Alerts
                  {notifications.length > 0 && (
                    <span style={{ marginLeft: 8, background: '#ef4444', borderRadius: 10, padding: '1px 7px', fontSize: 10, color: '#fff', fontWeight: 700 }}>
                      {notifications.length}
                    </span>
                  )}
                </div>
                {notifications.length > 0 && (
                  <button
                    onClick={() => setNotifications([])}
                    style={{ background: 'none', border: `1px solid ${bord}`, color: sub, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div style={{ maxHeight: '78vh', overflowY: 'auto', padding: notifications.length === 0 ? 0 : 8 }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: dark ? '#0f172a' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', fontSize: 20 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    </div>
                    <div style={{ color: sub, fontSize: 13 }}>No alerts</div>
                  </div>
                ) : (
                  notifications.map(n => (
                    <Toast key={n.id} note={n} onClose={() => setNotifications(p => p.filter(x => x.id !== n.id))} />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {logsFor && <LogsModal pod={logsFor} onClose={() => setLogsFor(null)} theme={theme} />}
    </div>
  );
}
